import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  Settings,
  Play,
  Pause,
  RefreshCw,
  Building2,
  Wrench,
  CalendarClock,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  FolderKanban,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily", days: 1 },
  { value: "weekly", label: "Weekly", days: 7 },
  { value: "biweekly", label: "Bi-weekly", days: 14 },
  { value: "monthly", label: "Monthly", days: 30 },
  { value: "quarterly", label: "Quarterly", days: 90 },
  { value: "biannual", label: "Bi-annual", days: 180 },
  { value: "yearly", label: "Yearly", days: 365 },
  { value: "custom", label: "Custom", days: 0 }
];

const REQUEST_TYPES = [
  { value: "preventive_maintenance", label: "Preventive Maintenance" },
  { value: "scheduled_inspection", label: "Scheduled Inspection" },
  { value: "filter_replacement", label: "Filter Replacement" },
  { value: "calibration", label: "Calibration" },
  { value: "water_quality_test", label: "Water Quality Test" },
  { value: "equipment_service", label: "Equipment Service" },
  { value: "other", label: "Other" }
];

export default function PMSchedulesManagement({ embedded = false, onConvertToProject }) {
  const { token } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [healthSystems, setHealthSystems] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [assets, setAssets] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingJobs, setProcessingJobs] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // list, calendar
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, overdue: 0, due_soon: 0 });
  const [convertingJobId, setConvertingJobId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    hospital_id: "",
    hospital_name: "",
    facility_id: "",
    facility_name: "",
    asset_id: "",
    asset_name: "",
    request_type: "preventive_maintenance",
    priority: "scheduled",
    description: "",
    frequency_type: "monthly",
    frequency_days: 30,
    next_due_date: "",
    advance_notice_days: 7,
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    assigned_contractor_id: "",
    auto_dispatch: false,
    is_active: true,
    notes: ""
  });

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API}/admin/scheduled-maintenance-jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
        setStats(data.stats || { total: 0, active: 0, overdue: 0, due_soon: 0 });
      }
    } catch (error) {
      console.error("Failed to fetch scheduled jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHealthSystems = async () => {
    try {
      const response = await fetch(`${API}/health-systems`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHealthSystems(data.health_systems || []);
      }
    } catch (error) {
      console.error("Failed to fetch health systems:", error);
    }
  };

  const fetchContractors = async () => {
    try {
      const response = await fetch(`${API}/contractors/profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContractors(data.contractors || []);
      }
    } catch (error) {
      console.error("Failed to fetch contractors:", error);
    }
  };

  const fetchCalendarEvents = async () => {
    try {
      const response = await fetch(
        `${API}/admin/scheduled-maintenance/calendar?month=${calendarMonth}&year=${calendarYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCalendarEvents(data.events || []);
      }
    } catch (error) {
      console.error("Failed to fetch calendar events:", error);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchHealthSystems();
    fetchContractors();
  }, [token]);

  useEffect(() => {
    if (viewMode === "calendar") {
      fetchCalendarEvents();
    }
  }, [viewMode, calendarMonth, calendarYear]);

  const handleSelectHealthSystem = (systemId) => {
    const system = healthSystems.find(h => h.id === systemId);
    if (system) {
      setFormData({
        ...formData,
        hospital_id: systemId,
        hospital_name: system.name,
        facility_id: "",
        facility_name: "",
        contact_email: system.primary_contact_email || "",
        contact_name: system.primary_contact_name || "",
        contact_phone: system.primary_contact_phone || ""
      });
      setFacilities(system.facilities || []);
      // Fetch assets for this system
      fetchAssets(systemId);
    }
  };

  const handleSelectFacility = (facilityId) => {
    const facility = facilities.find(f => f.id === facilityId);
    if (facility) {
      setFormData({
        ...formData,
        facility_id: facilityId,
        facility_name: facility.name
      });
    }
  };

  const fetchAssets = async (hospitalId) => {
    try {
      const response = await fetch(`${API}/assets?hospital_id=${hospitalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssets(data.assets || []);
      }
    } catch (error) {
      console.error("Failed to fetch assets:", error);
    }
  };

  const handleFrequencyChange = (frequencyType) => {
    const option = FREQUENCY_OPTIONS.find(f => f.value === frequencyType);
    setFormData({
      ...formData,
      frequency_type: frequencyType,
      frequency_days: option?.days || formData.frequency_days
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.hospital_id || !formData.name || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      const url = editingJob 
        ? `${API}/admin/scheduled-maintenance-jobs/${editingJob.id}` 
        : `${API}/admin/scheduled-maintenance-jobs`;
      const method = editingJob ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingJob ? "Schedule updated successfully" : "Schedule created successfully");
        setShowCreateDialog(false);
        setEditingJob(null);
        resetForm();
        fetchJobs();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Operation failed");
      }
    } catch (error) {
      toast.error("Failed to save schedule");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!confirm("Are you sure you want to delete this scheduled job?")) return;

    try {
      const response = await fetch(`${API}/admin/scheduled-maintenance-jobs/${jobId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success("Schedule deleted");
        fetchJobs();
      }
    } catch (error) {
      toast.error("Failed to delete schedule");
    }
  };

  // Convert PM Schedule to Project
  const handleConvertToProject = async (job) => {
    setConvertingJobId(job.id);
    try {
      // Create project from PM schedule
      const projectData = {
        name: `${job.name} - Project`,
        description: `Project created from PM Schedule: ${job.description || job.name}. Hospital: ${job.hospital_name || 'N/A'}. Frequency: ${job.frequency_type}`,
        hospital_id: job.hospital_id,
        hospital_name: job.hospital_name,
        facility_id: job.facility_id,
        facility_name: job.facility_name,
        budget: 0,
        project_type: "maintenance",
        compliance_requirements: []
      };

      const response = await fetch(`${API}/project-command/projects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(projectData)
      });

      if (response.ok) {
        const data = await response.json();
        const projectId = data.project?.id;

        // Create initial phase based on the PM schedule type
        const phaseData = {
          project_id: projectId,
          name: getPhaseNameFromRequestType(job.request_type),
          description: job.description || `Scheduled ${job.request_type?.replace(/_/g, " ")}`,
          status: "upcoming",
          lead_time_weeks: Math.ceil(job.frequency_days / 7) || 4,
          budget: 0,
          risk_level: job.priority === "urgent" ? "high" : "low",
          compliance_tags: getComplianceTagsFromType(job.request_type),
          order: 0
        };

        await fetch(`${API}/project-command/phases`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(phaseData)
        });

        toast.success(`Project created: ${data.project?.name}`, {
          description: "Switch to Projects tab to manage it",
          action: onConvertToProject ? {
            label: "View Project",
            onClick: () => onConvertToProject(projectId)
          } : undefined
        });

        // If callback provided, switch to projects tab
        if (onConvertToProject) {
          onConvertToProject(projectId);
        }
      } else {
        throw new Error("Failed to create project");
      }
    } catch (error) {
      console.error("Convert to project error:", error);
      toast.error("Failed to convert to project");
    } finally {
      setConvertingJobId(null);
    }
  };

  // Helper function to get phase name from request type
  const getPhaseNameFromRequestType = (requestType) => {
    const typeNames = {
      preventive_maintenance: "Preventive Maintenance",
      scheduled_inspection: "Scheduled Inspection",
      filter_replacement: "Filter Replacement",
      calibration: "Equipment Calibration",
      water_quality_test: "Water Quality Testing",
      equipment_service: "Equipment Service",
      other: "Maintenance Task"
    };
    return typeNames[requestType] || "Maintenance Phase";
  };

  // Helper function to get compliance tags based on type
  const getComplianceTagsFromType = (requestType) => {
    const typeTags = {
      water_quality_test: ["AAMI ST108", "CMS"],
      filter_replacement: ["ICRA Class I"],
      calibration: ["Joint Commission"],
      scheduled_inspection: ["HHSC", "Joint Commission"],
      preventive_maintenance: ["Joint Commission"],
      equipment_service: ["Joint Commission"]
    };
    return typeTags[requestType] || [];
  };

  const toggleJobStatus = async (job) => {
    try {
      const response = await fetch(`${API}/admin/scheduled-maintenance-jobs/${job.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !job.is_active })
      });
      if (response.ok) {
        toast.success(job.is_active ? "Schedule paused" : "Schedule activated");
        fetchJobs();
      }
    } catch (error) {
      toast.error("Failed to update schedule");
    }
  };

  const handleGenerateRequest = async (jobId) => {
    try {
      const response = await fetch(`${API}/admin/scheduled-maintenance-jobs/${jobId}/generate-request`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Request ${data.request_id} created!`);
        fetchJobs();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to generate request");
      }
    } catch (error) {
      toast.error("Failed to generate request");
    }
  };

  const handleProcessDueJobs = async () => {
    setProcessingJobs(true);
    try {
      const response = await fetch(`${API}/admin/scheduled-maintenance/process-due-jobs`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Processed: ${data.generated_count} requests generated, ${data.skipped_count} skipped`);
        fetchJobs();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to process jobs");
      }
    } catch (error) {
      toast.error("Failed to process due jobs");
    } finally {
      setProcessingJobs(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      hospital_id: "",
      hospital_name: "",
      facility_id: "",
      facility_name: "",
      asset_id: "",
      asset_name: "",
      request_type: "preventive_maintenance",
      priority: "scheduled",
      description: "",
      frequency_type: "monthly",
      frequency_days: 30,
      next_due_date: "",
      advance_notice_days: 7,
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      assigned_contractor_id: "",
      auto_dispatch: false,
      is_active: true,
      notes: ""
    });
    setFacilities([]);
    setAssets([]);
  };

  const openEditDialog = (job) => {
    setEditingJob(job);
    setFormData({
      name: job.name || "",
      hospital_id: job.hospital_id || "",
      hospital_name: job.hospital_name || "",
      facility_id: job.facility_id || "",
      facility_name: job.facility_name || "",
      asset_id: job.asset_id || "",
      asset_name: job.asset_name || "",
      request_type: job.request_type || "preventive_maintenance",
      priority: job.priority || "scheduled",
      description: job.description || "",
      frequency_type: job.frequency_type || "monthly",
      frequency_days: job.frequency_days || 30,
      next_due_date: job.next_due_date ? job.next_due_date.split("T")[0] : "",
      advance_notice_days: job.advance_notice_days || 7,
      contact_name: job.contact_name || "",
      contact_email: job.contact_email || "",
      contact_phone: job.contact_phone || "",
      assigned_contractor_id: job.assigned_contractor_id || "",
      auto_dispatch: job.auto_dispatch || false,
      is_active: job.is_active !== false,
      notes: job.notes || ""
    });
    // Load facilities and assets for the selected hospital
    const system = healthSystems.find(h => h.id === job.hospital_id);
    if (system) {
      setFacilities(system.facilities || []);
      fetchAssets(job.hospital_id);
    }
    setShowCreateDialog(true);
  };

  const getDueStatus = (daysUntilDue, isOverdue) => {
    if (daysUntilDue === null || daysUntilDue === undefined) {
      return { label: "No date", color: "bg-slate-100 text-slate-600" };
    }
    if (isOverdue) return { label: `${Math.abs(daysUntilDue)}d overdue`, color: "bg-red-100 text-red-700" };
    if (daysUntilDue <= 7) return { label: `Due in ${daysUntilDue}d`, color: "bg-amber-100 text-amber-700" };
    if (daysUntilDue <= 30) return { label: `Due in ${daysUntilDue}d`, color: "bg-blue-100 text-blue-700" };
    return { label: `Due in ${daysUntilDue}d`, color: "bg-emerald-100 text-emerald-700" };
  };

  // Calendar helpers
  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month - 1, 1).getDay();
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const navigateMonth = (direction) => {
    if (direction === "prev") {
      if (calendarMonth === 1) {
        setCalendarMonth(12);
        setCalendarYear(calendarYear - 1);
      } else {
        setCalendarMonth(calendarMonth - 1);
      }
    } else {
      if (calendarMonth === 12) {
        setCalendarMonth(1);
        setCalendarYear(calendarYear + 1);
      } else {
        setCalendarMonth(calendarMonth + 1);
      }
    }
  };

  const getEventsForDay = (day) => {
    const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarEvents.filter(e => e.date && e.date.startsWith(dateStr));
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - hidden when embedded */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Scheduled Maintenance</h2>
            <p className="text-slate-500">Manage recurring preventive maintenance jobs</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={handleProcessDueJobs}
              disabled={processingJobs}
              data-testid="process-due-jobs-btn"
            >
              {processingJobs ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Process Due Jobs
            </Button>
            <Button 
              onClick={() => { resetForm(); setEditingJob(null); setShowCreateDialog(true); }} 
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-scheduled-job-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Schedule
            </Button>
          </div>
        </div>
      )}

      {/* Action buttons when embedded */}
      {embedded && (
        <div className="flex justify-end gap-2">
          <Button 
            variant="outline" 
            onClick={handleProcessDueJobs}
            disabled={processingJobs}
            data-testid="process-due-jobs-btn"
          >
            {processingJobs ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Process Due Jobs
          </Button>
          <Button 
            onClick={() => { resetForm(); setEditingJob(null); setShowCreateDialog(true); }} 
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-scheduled-job-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Schedule
          </Button>
        </div>
      )}

      {/* View Toggle & Stats */}
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.active}</p>
                <p className="text-sm text-blue-600">Active</p>
              </div>
            </div>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.overdue}</p>
                <p className="text-sm text-red-600">Overdue</p>
              </div>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.due_soon}</p>
                <p className="text-sm text-amber-600">Due Soon</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Pause className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-700">{stats.total - stats.active}</p>
                <p className="text-sm text-slate-600">Paused</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            variant={viewMode === "list" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("list")}
            className={viewMode === "list" ? "bg-blue-600" : ""}
          >
            <Wrench className="w-4 h-4 mr-1" />
            List
          </Button>
          <Button 
            variant={viewMode === "calendar" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("calendar")}
            className={viewMode === "calendar" ? "bg-blue-600" : ""}
          >
            <CalendarDays className="w-4 h-4 mr-1" />
            Calendar
          </Button>
        </div>
      </div>

      {viewMode === "list" ? (
        /* List View */
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Job Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Hospital</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Frequency</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Next Due</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((job) => {
                const dueStatus = getDueStatus(job.days_until_due, job.is_overdue);
                return (
                  <tr key={job.id} className="hover:bg-slate-50" data-testid={`scheduled-job-${job.id}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{job.name}</p>
                        {job.asset_name && (
                          <p className="text-xs text-slate-500">Asset: {job.asset_name}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-900">{job.hospital_name}</p>
                      {job.facility_name && (
                        <p className="text-xs text-slate-500">{job.facility_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">
                        {job.request_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">
                        {job.frequency_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                        {job.frequency_type === "custom" && ` (${job.frequency_days}d)`}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${dueStatus.color}`}>
                        {dueStatus.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {job.is_active ? (
                        <span className="flex items-center gap-1 text-emerald-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-slate-400 text-sm">
                          <Pause className="w-4 h-4" />
                          Paused
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-purple-600 hover:bg-purple-50"
                          onClick={() => handleConvertToProject(job)}
                          disabled={convertingJobId === job.id}
                          title="Convert to Project"
                          data-testid={`convert-to-project-${job.id}`}
                        >
                          {convertingJobId === job.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <FolderKanban className="w-4 h-4" />
                          )}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={() => handleGenerateRequest(job.id)}
                          title="Generate request now"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleJobStatus(job)}>
                          {job.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(job)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(job.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {jobs.length === 0 && (
            <div className="py-8 text-center text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No scheduled maintenance jobs configured</p>
              <Button onClick={() => setShowCreateDialog(true)} className="mt-4" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create First Schedule
              </Button>
            </div>
          )}
        </div>
      ) : (
        /* Calendar View */
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigateMonth("prev")}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h3 className="text-lg font-semibold text-slate-900">
              {monthNames[calendarMonth - 1]} {calendarYear}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => navigateMonth("next")}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
            {/* Day Headers */}
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
              <div key={day} className="bg-slate-50 p-2 text-center text-xs font-medium text-slate-600">
                {day}
              </div>
            ))}
            
            {/* Empty cells for days before start of month */}
            {Array.from({ length: getFirstDayOfMonth(calendarMonth, calendarYear) }).map((_, i) => (
              <div key={`empty-${i}`} className="bg-slate-50 min-h-[80px]" />
            ))}
            
            {/* Days of month */}
            {Array.from({ length: getDaysInMonth(calendarMonth, calendarYear) }).map((_, i) => {
              const day = i + 1;
              const events = getEventsForDay(day);
              const isToday = new Date().getDate() === day && 
                             new Date().getMonth() + 1 === calendarMonth && 
                             new Date().getFullYear() === calendarYear;
              
              return (
                <div 
                  key={day} 
                  className={`bg-white min-h-[80px] p-1 ${isToday ? "bg-blue-50" : ""}`}
                >
                  <p className={`text-xs font-medium mb-1 ${isToday ? "text-blue-600" : "text-slate-600"}`}>
                    {day}
                  </p>
                  <div className="space-y-1">
                    {events.slice(0, 3).map((event, idx) => (
                      <div 
                        key={idx}
                        className={`text-xs truncate px-1 py-0.5 rounded ${
                          event.type === "scheduled" 
                            ? "bg-emerald-100 text-emerald-700" 
                            : "bg-blue-100 text-blue-700"
                        }`}
                        title={event.title}
                      >
                        {event.title}
                      </div>
                    ))}
                    {events.length > 3 && (
                      <p className="text-xs text-slate-400 px-1">+{events.length - 3} more</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 text-xs text-slate-600">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-emerald-100 rounded" />
              <span>Scheduled Job</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-100 rounded" />
              <span>Maintenance Request</span>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-blue-600" />
              {editingJob ? "Edit Scheduled Job" : "Create Scheduled Job"}
            </DialogTitle>
            <DialogDescription>
              Set up recurring preventive maintenance
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Job Name */}
            <div>
              <Label>Job Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Monthly RO System Filter Change"
                required
              />
            </div>

            {/* Hospital & Facility */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Health System / Hospital *</Label>
                <Select value={formData.hospital_id} onValueChange={handleSelectHealthSystem}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {healthSystems.map(h => (
                      <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Facility (Optional)</Label>
                <Select 
                  value={formData.facility_id} 
                  onValueChange={handleSelectFacility}
                  disabled={!formData.hospital_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {facilities.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Asset Selection */}
            {assets.length > 0 && (
              <div>
                <Label>Related Asset (Optional)</Label>
                <Select 
                  value={formData.asset_id} 
                  onValueChange={(v) => {
                    const asset = assets.find(a => a.id === v);
                    setFormData({...formData, asset_id: v, asset_name: asset?.name || ""});
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an asset..." />
                  </SelectTrigger>
                  <SelectContent>
                    {assets.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name} {a.serial_number ? `(${a.serial_number})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Request Type & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Request Type</Label>
                <Select value={formData.request_type} onValueChange={(v) => setFormData({...formData, request_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REQUEST_TYPES.map(rt => (
                      <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the maintenance work..."
                rows={3}
                required
              />
            </div>

            {/* Frequency */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Frequency</Label>
                <Select value={formData.frequency_type} onValueChange={handleFrequencyChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCY_OPTIONS.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.frequency_type === "custom" && (
                <div>
                  <Label>Days</Label>
                  <Input
                    type="number"
                    value={formData.frequency_days}
                    onChange={(e) => setFormData({...formData, frequency_days: parseInt(e.target.value) || 30})}
                    min="1"
                  />
                </div>
              )}
              <div>
                <Label>Next Due Date *</Label>
                <Input
                  type="date"
                  value={formData.next_due_date}
                  onChange={(e) => setFormData({...formData, next_due_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Advance Notice (days)</Label>
                <Input
                  type="number"
                  value={formData.advance_notice_days}
                  onChange={(e) => setFormData({...formData, advance_notice_days: parseInt(e.target.value) || 7})}
                  min="0"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-3">Contact Information</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    placeholder="Name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    placeholder="email@hospital.com"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    placeholder="(800) 988-3892"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingJob ? "Update" : "Create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
